/** @file pointerInput: viewport mouse rules (select, gizmo/quad drags, double-click, camera). */
import { beginEdit } from './beginEdit';
import { beginQuadEdit } from './beginQuadEdit';
import { cssToTarget } from './cssToTarget';
import { endQuadEdit } from './endQuadEdit';
import { gizmoHit } from './gizmoHit';
import { jointMenu } from './jointMenu';
import { orbitInput } from './orbitInput';
import { nodeAt } from './nodeAt';
import { openTileAt } from './openTileAt';
import { QUAD_HANDLE_METRICS } from './quadHandleMetrics';
import { quadAt } from './quadAt';
import { quadHandleAt } from './quadHandleAt';
import { selectAt } from './selectAt';
import { selectedQuad } from './selectedQuad';
import { shortestYawDelta } from './shortestYawDelta';
import { stepQuadEdit } from './stepQuadEdit';
import { targetPoint } from './targetPoint';
import type { ActiveEdit, ViewportCtx } from './viewportCtx';

/**
 * A camera drag in progress: which button started it, the last pointer position (target pixels), the travel so far;
 * `press` is where the last left press that was not a camera gesture went down (a click when it comes up nearby).
 */
type CameraDrag = { on: boolean; button: number; last: [number, number]; travel: number; press: [number, number] | null };

/** A double-click: two left clicks within this many ms and target px of each other (each with no drag between). */
const DOUBLE_MS = 350;
const DOUBLE_PX = 4;

/** A right drag that travelled this many target pixels or more was an orbit, not a click: no context menu. */
const MENU_TRAVEL_PX = 3;

/** The pointer in CSS px from the viewport box's top-left (where the labels are placed). */
function cssCursor(ctx: ViewportCtx, e: MouseEvent): [number, number] {
  const r = ctx.container.getBoundingClientRect();
  return [e.clientX - r.left, e.clientY - r.top];
}

/** The tool the mouse rules see: turntable mode behaves like the hand tool. */
const toolOf = (ctx: ViewportCtx) => (ctx.mode === 'turntable' ? 'hand' : ctx.store.getState().tool);

/** Applies a camera drag step (target-pixel deltas) through the store (yaw / pitch) and the camera control (pan). */
function cameraStep(ctx: ViewportCtx, e: PointerEvent, drag: CameraDrag, dx: number, dy: number): void {
  const s = ctx.store.getState();
  const m = orbitInput({ kind: 'drag', button: drag.button, alt: e.altKey, shift: e.shiftKey, tool: toolOf(ctx), dx, dy });
  if (m === null) return;
  if (m.pan[0] !== 0 || m.pan[1] !== 0) ctx.camera?.pan(m.pan[0], m.pan[1]);
  if (m.orbit === 0 && m.tilt === 0) return;
  const [lo, hi] = ctx.profile?.camera.pitchRange ?? [-90, 90];
  s.setCamera({ yawDeg: shortestYawDelta(0, s.camera.yawDeg + m.orbit), pitchDeg: Math.min(hi, Math.max(lo, s.camera.pitchDeg + m.tilt)) });
}

/** Ends a gizmo gesture: one `clip.setPose` when the pointer moved, the override cleared once it lands. */
function endEdit(ctx: ViewportCtx): void {
  const active = ctx.edit;
  ctx.edit = null;
  const s = ctx.store.getState();
  const nodes = s.poseOverride?.nodes;
  if (active === null || !active.moved || nodes === undefined) { s.setPoseOverride(null); return; }
  void s.dispatch([active.edit.command(nodes)], 'Pose joint')
    .then(() => { ctx.holdOverrideUntil = ctx.store.getState().puppet.revision; })
    .catch(() => { ctx.store.getState().setPoseOverride(null); });
}

/** Pointer down: camera gestures first (a right drag orbits; a right click opens the menu); then a quad handle, a gizmo / IK grab; else select. */
function down(ctx: ViewportCtx, e: PointerEvent, drag: CameraDrag): void {
  ctx.store.getState().closeMenu();
  const p = targetPoint(ctx, e);
  ctx.quad.pointerIn = true;
  const isCamera = orbitInput({ kind: 'drag', button: e.button, alt: e.altKey, shift: e.shiftKey, tool: toolOf(ctx), dx: 0, dy: 0 }) !== null;
  ctx.container.setPointerCapture(e.pointerId);
  if (isCamera) { Object.assign(drag, { on: true, button: e.button, last: p, travel: 0, press: null }); return; }
  if (e.button !== 0 || ctx.mode !== 'edit') return;
  drag.press = p;
  if (!beginQuadEdit(ctx, p) && !beginEdit(ctx, p)) selectAt(ctx, p, e.shiftKey);
}

/** Left button up: a click (no drag) right after another one nearby opens the part's Tile tab (`openTileAt`). */
function click(ctx: ViewportCtx, e: PointerEvent, drag: CameraDrag): void {
  const p = targetPoint(ctx, e);
  const press = drag.press;
  drag.press = null;
  if (press === null || Math.hypot(p[0] - press[0], p[1] - press[1]) > DOUBLE_PX) { ctx.quad.lastClick = null; return; }
  const last = ctx.quad.lastClick;
  const double = last !== null && e.timeStamp - last.t <= DOUBLE_MS && Math.hypot(p[0] - last.p[0], p[1] - last.p[1]) <= DOUBLE_PX;
  ctx.quad.lastClick = double ? null : { t: e.timeStamp, p };
  if (double) openTileAt(ctx, p);
}

/** The quads view's hover: the selected part's handle under `p`, else the part under it. */
function quadHover(ctx: ViewportCtx, p: [number, number]): void {
  const on = ctx.mode === 'edit' && ctx.store.getState().camera.quads;
  const q = on ? selectedQuad(ctx) : null;
  ctx.quad.handle = q === null ? null : quadHandleAt(q.points, p, QUAD_HANDLE_METRICS.grab * cssToTarget(ctx));
  ctx.quad.hover = on ? quadAt(ctx, p) : -1;
}

/** The hover highlights under `p`: a quad handle (else a gizmo handle), the quad, the node. */
function hoverAt(ctx: ViewportCtx, p: [number, number]): void {
  quadHover(ctx, p);
  ctx.hover = ctx.quad.handle === null ? (gizmoHit(ctx, p)?.axis ?? null) : null;
  ctx.hoverNode = ctx.hover === null && ctx.mode === 'edit' ? (nodeAt(ctx, p)?.index ?? null) : null;
}

/** A rotate drag's arc follows the measured turn, snapped as the key is (`applyDragToKey`). */
function sweep(edit: ActiveEdit, snapDeg: number): void {
  const drag = edit.edit.lastDrag();
  if (edit.arc === null || drag?.kind !== 'rotate') return;
  edit.arc.sweepDeg = snapDeg > 0 ? Math.round(drag.deltaDeg / snapDeg) * snapDeg : drag.deltaDeg;
}

/** A gizmo gesture step: the keys under the pointer shown through `setPoseOverride`, the readout and the arc. */
function editStep(ctx: ViewportCtx, edit: ActiveEdit, e: PointerEvent, p: [number, number]): void {
  const s = ctx.store.getState();
  const snap = e.ctrlKey ? s.snapDeg : 0;
  const nodes = edit.edit.keys(p, snap);
  Object.assign(edit, { moved: true, readout: edit.edit.readout(nodes, snap), cursor: cssCursor(ctx, e) });
  s.setPoseOverride({ clip: edit.clip, frame: edit.frame, nodes });
  sweep(edit, snap);
}

/** Pointer move: camera step, quad drag step, gizmo gesture step (shown through `setPoseOverride`), or the hover highlights. */
function move(ctx: ViewportCtx, e: PointerEvent, drag: CameraDrag): void {
  const p = targetPoint(ctx, e);
  if (drag.on) {
    const dx = p[0] - drag.last[0], dy = p[1] - drag.last[1];
    drag.travel += Math.hypot(dx, dy);
    cameraStep(ctx, e, drag, dx, dy);
    drag.last = p;
    return;
  }
  if (ctx.quad.edit !== null) { stepQuadEdit(ctx.quad.edit, p); return; }
  if (ctx.edit !== null) { editStep(ctx, ctx.edit, e, p); return; }
  hoverAt(ctx, p);
}

/** Pointer up: the camera drag ends, a gizmo or quad gesture commits, a left click may be the second of a double-click. */
function up(ctx: ViewportCtx, e: PointerEvent, drag: CameraDrag): void {
  drag.on = false;
  if (ctx.quad.edit !== null) void endQuadEdit(ctx);
  if (ctx.edit !== null) endEdit(ctx);
  if (e.button === 0) click(ctx, e, drag);
}

/** Right click (not a right drag that orbited): select the joint under the pointer (keeping a selection that has it) and open its menu. */
function menu(ctx: ViewportCtx, e: MouseEvent, handlers: Record<string, () => void>, drag: CameraDrag): void {
  e.preventDefault();
  if (drag.button === 2 && drag.travel >= MENU_TRAVEL_PX) return;
  const s = ctx.store.getState();
  const p = targetPoint(ctx, e);
  const joint = ctx.mode === 'edit' ? nodeAt(ctx, p) : null;
  if (joint !== null && !s.selection.nodes.includes(joint.name)) s.select({ nodes: [joint.name] });
  s.openMenu(e.clientX, e.clientY, jointMenu(ctx, joint?.name ?? null, handlers));
}

/** Attaches the listeners to `ctx.container`; returns their removal. */
export function pointerInput(ctx: ViewportCtx, handlers: Record<string, () => void>): () => void {
  const drag: CameraDrag = { on: false, button: 0, last: [0, 0], travel: 0, press: null };
  const listeners: [string, (e: never) => void][] = [
    ['pointerdown', (e: PointerEvent) => { down(ctx, e, drag); }],
    ['pointermove', (e: PointerEvent) => { move(ctx, e, drag); }],
    ['pointerup', (e: PointerEvent) => { up(ctx, e, drag); }],
    ['pointercancel', () => { drag.on = false; drag.press = null; if (ctx.quad.edit !== null) void endQuadEdit(ctx); if (ctx.edit !== null) endEdit(ctx); }],
    ['pointerenter', () => { ctx.quad.pointerIn = true; }],
    ['pointerleave', () => {
      ctx.quad.pointerIn = false;
      if (ctx.edit === null && ctx.quad.edit === null) { ctx.hover = null; ctx.hoverNode = null; ctx.quad.hover = -1; ctx.quad.handle = null; }
    }],
    ['wheel', (e: WheelEvent) => {
      e.preventDefault();
      const m = orbitInput({ kind: 'wheel', deltaY: e.deltaY });
      if (m !== null) ctx.camera?.zoom(m.zoom);
    }],
    ['contextmenu', (e: MouseEvent) => { menu(ctx, e, handlers, drag); }],
  ];
  for (const [type, fn] of listeners) ctx.container.addEventListener(type, fn as EventListener, { passive: false });
  return () => { for (const [type, fn] of listeners) ctx.container.removeEventListener(type, fn as EventListener); };
}
